import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import Footer from '../../Footer/Footer';
import styled from 'styled-components';
import ImageSlantPricing from '../../common/image-slant-pricing';
import WhatsOn from '../../common/What-on';
import OpeningTimes from '../../common/Opening-times';
import { getCastleOpeningDisplay } from '../../../utils/openingHoursUtils';
import Hero from '../../common/Hero';
import { processEvents, GET_EVENTS_QUERY } from '../../../utils/eventUtils';

import castleImage from '../../../assets/castle.png';
import gardensImage from '../../../assets/gardens.png';
import retailImage from '../../../assets/retail.png';
import playgroundImage from '../../../assets/play.png';
import christmasImage from '../../../assets/christmas25/festive.webp';

/* Bunny CDN — banner + ticket row imagery */
const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';
const DAY_TICKETS_CASTLE_TICKET_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/CJP%20Belvoir%20Castle-255%20(1).webp';
const DAY_TICKETS_GARDENS_TICKET_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/CJP%20Belvoir%20Castle-267%20(1).webp';
const DAY_TICKETS_ANIMAL_PARK_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/IMG_1835%20(1).webp';

const GET_TICKET_PRICING = gql`
  query GetTicketPricing {
    siteSettings(where: {title: "Global Site Settings"}) {
      nodes {
        siteSettingsFields {
          ticketPricing {
            castleGardensPricing {
              adultType
              adultOnlinePrice
              adultOfficePrice
              childType
              childOnlinePrice
              childOfficePrice
              concessionType
              concessionOnlinePrice
              concessionOfficePrice
            }
            gardensPricing {
              adultType
              adultOnlinePrice
              adultOfficePrice
              childType
              childOnlinePrice
              childOfficePrice
              concessionType
              concessionOnlinePrice
              concessionOfficePrice
            }
          }
        }
      }
    }
  }
`;

// Simple query to explore the schema
const GET_SITE_SETTINGS = gql`
  query GetSiteSettings {
    siteSettings {
      nodes {
        title
        siteSettingsFields {
          openingHours {
            castle {
              days
              times
              note
            }
            gardens {
              days
              times
              note
            }
            retail {
              days
              times
              note
            }
          }
        }
      }
    }
  }
`;

const DayTicketsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const TicketsIntroSection = styled.section`
  padding: clamp(2rem, 4.5vw, 3rem) clamp(1.25rem, 4vw, 2rem)
    clamp(1.25rem, 3vw, 1.75rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  background: linear-gradient(
    180deg,
    ${props => props.theme.colors.offWhite} 0%,
    rgba(241, 239, 234, 0.35) 100%
  );
`;

const TicketsIntroInner = styled.div`
  max-width: 38rem;
  margin: 0 auto;
  text-align: center;
`;

const TicketsIntroEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.48);
  margin: 0 0 0.65rem;
`;

const TicketsIntroTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.75rem, 4vw, 2.35rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.85rem;
  line-height: 1.12;
`;

const TicketsIntroSubtitle = styled.p`
  font-size: clamp(0.94rem, 1.85vw, 1.05rem);
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.58);
  margin: 0;
`;

const EventsLoadingState = styled.p`
  text-align: center;
  margin: 0;
  padding: clamp(2.5rem, 6vw, 3.5rem) 1.25rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1rem;
  color: rgba(16, 29, 64, 0.55);
  background: ${props => props.theme.colors.offWhite};
  scroll-margin-top: 120px;

  @media (max-width: 768px) {
    scroll-margin-top: 96px;
  }
`;

const EventsErrorState = styled(EventsLoadingState)`
  color: rgba(16, 29, 64, 0.72);
`;

const PackageSection = styled.section`
  margin: 0;
  padding: clamp(3rem, 7vw, 4.5rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  background: ${props => props.theme.colors.offWhite};
`;

const PackageInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

const PackageHeader = styled.div`
  text-align: center;
  max-width: 38rem;
  margin: 0 auto clamp(2.25rem, 5vw, 3rem);
`;

const PackageEyebrowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
`;

const PackageEyebrowLine = styled.span`
  display: block;
  width: 2rem;
  height: 1.5px;
  background: ${({ theme }) => theme.colors.accent};
  border-radius: 2px;
  opacity: 0.85;
`;

const PackageEyebrow = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.48);
`;

const PackageTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.85rem, 4vw, 2.45rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  line-height: 1.12;

  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 2px;
    margin: 1rem auto 0;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.accent},
      transparent
    );
  }
`;

const PackageDescription = styled.p`
  font-size: clamp(0.95rem, 1.85vw, 1.05rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.58);
  margin: 1.25rem 0 0;
`;

const ExtrasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ExtraCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 1.35rem 1.35rem 1.25rem;
  border-radius: 16px;
  border: 1px solid rgba(16, 29, 64, 0.08);
  background: #fff;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 12px 36px rgba(16, 29, 64, 0.07);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(157, 109, 73, 0.28);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 18px 44px rgba(16, 29, 64, 0.1);
  }
`;

const ExtraTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.15rem, 2.2vw, 1.3rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.55rem;
  line-height: 1.2;
`;

const ExtraDescription = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.9rem, 1.7vw, 0.98rem);
  line-height: 1.58;
  color: rgba(16, 29, 64, 0.62);
  margin: 0 0 1rem;
  flex: 1;
`;

const ExtraLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: auto;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${props => props.theme.colors.accent};
  transition: color 0.2s ease, gap 0.2s ease;

  &::after {
    content: '→';
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${props => props.theme.colors.primary};

    &::after {
      transform: translateX(3px);
    }
  }
`;

const VISIT_EXTRAS = [
  {
    title: 'Afternoon Tea',
    description:
      'Indulge in exquisite Afternoon Tea in the elegant Aviary Tearoom — reserve when you book or visit the dining pages.',
    to: '/castle-dining/afternoon-tea',
  },
  {
    title: 'Guided Tours',
    description:
      'Join expert guides for insights into the Castle’s history, collections and stories.',
    to: '/your-visit/tours',
  },
  {
    title: 'Animal Park',
    description:
      'Meet friendly animals at the Belvoir Animal Park in the Retail Village — perfect with a gardens ticket.',
    to: '/retail-village/animal-park',
  },
  {
    title: 'Seasonal Activities',
    description:
      'Workshops, trails and special seasonal moments throughout the year — see what’s on for your dates.',
    to: '/whats-on/seasonal-activities',
  },
];

// ── CHILDREN'S SUMMER RETURN OFFER BANNER ──────────────────────────────────

const ChildrenReturnBanner = styled.section`
  background: linear-gradient(135deg, #101d40 0%, #1a3352 100%);
  padding: clamp(2rem, 5vw, 2.75rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const ChildrenReturnInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.75rem 2.5rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
`;

const ChildrenReturnAccent = styled.div`
  width: 3px;
  align-self: stretch;
  background: linear-gradient(180deg, #9d6d49 0%, rgba(157, 109, 73, 0.3) 100%);
  border-radius: 3px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 48px;
    height: 3px;
    align-self: auto;
  }
`;

const ChildrenReturnCopy = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChildrenReturnEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #9d6d49;
  margin: 0 0 0.5rem;
`;

const ChildrenReturnHeadline = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.2rem, 2.8vw, 1.6rem);
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.55rem;
  line-height: 1.2;
`;

const ChildrenReturnSubtext = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.875rem, 1.7vw, 0.95rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
`;

const ChildrenReturnCTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0.85rem 1.65rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: #101d40;
  background: #ffffff;
  border: 2px solid #ffffff;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #9d6d49;
    border-color: #9d6d49;
    color: #ffffff;
  }
`;

// ── END CHILDREN'S RETURN BANNER STYLES ────────────────────────────────────

// Styled note for ticket types
const EventsBridgeSection = styled.section`
  padding: clamp(2.25rem, 5vw, 3.25rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  background: linear-gradient(
    180deg,
    ${props => props.theme.colors.offWhite} 0%,
    rgba(241, 239, 234, 0.88) 50%,
    ${props => props.theme.colors.offWhite} 100%
  );
`;

const EventsBridgeInner = styled.div`
  max-width: 40rem;
  margin: 0 auto;
  text-align: center;
`;

const EventsBridgeEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.48);
  margin: 0 0 0.65rem;
`;

const EventsBridgeTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  line-height: 1.15;
`;

const EventsBridgeCopy = styled.p`
  font-size: clamp(0.92rem, 1.8vw, 1rem);
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.58);
  margin: 0 auto 1.5rem;
  max-width: 32rem;
`;

const EventsBridgeButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.75rem 1rem;
`;

const EventsListRegion = styled.div`
  scroll-margin-top: 120px;

  @media (max-width: 768px) {
    scroll-margin-top: 96px;
  }
`;

const EventsPrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.5rem;
  border-radius: 999px;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: #fff;
  background: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    color: #fff;
    transform: translateY(-1px);
  }
`;

const EventsSecondaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.5rem;
  border-radius: 999px;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${props => props.theme.colors.primary};
  background: transparent;
  border: 2px solid rgba(16, 29, 64, 0.22);
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    color: ${props => props.theme.colors.accent};
    background: rgba(255, 255, 255, 0.65);
  }
`;

// ── JUNE TICKET PICKER (modal overlay) ──────────────────────────────────────

const JunePickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 29, 64, 0.55);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const JunePickerModal = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: clamp(1.75rem, 4vw, 2.5rem);
  max-width: 560px;
  width: 100%;
  box-shadow: 0 24px 80px rgba(16, 29, 64, 0.22);
  position: relative;
`;

const JunePickerClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(16, 29, 64, 0.4);
  font-size: 1.4rem;
  line-height: 1;
  padding: 0.25rem;
  &:hover { color: #101d40; }
`;

const JunePickerEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #9d6d49;
  margin: 0 0 0.4rem;
  text-align: center;
`;

const JunePickerTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  font-weight: 700;
  color: #101d40;
  margin: 0 0 0.35rem;
  text-align: center;
  line-height: 1.2;
`;

const JunePickerSub = styled.p`
  font-size: 0.88rem;
  color: rgba(16, 29, 64, 0.55);
  text-align: center;
  margin: 0 0 1.5rem;
  line-height: 1.5;
`;

const JunePickerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const JunePickerCard = styled.a`
  display: flex;
  flex-direction: column;
  padding: 1.35rem 1.25rem;
  border-radius: 14px;
  border: 2px solid rgba(16, 29, 64, 0.1);
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  background: #fff;

  &:hover {
    border-color: #9d6d49;
    box-shadow: 0 6px 24px rgba(16, 29, 64, 0.1);
    transform: translateY(-2px);
  }
`;

const JunePickerCardTitle = styled.span`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1rem;
  font-weight: 700;
  color: #101d40;
  margin-bottom: 0.3rem;
  display: block;
`;

const JunePickerCardDesc = styled.span`
  font-size: 0.8rem;
  color: rgba(16, 29, 64, 0.55);
  line-height: 1.45;
  display: block;
  margin-bottom: 0.9rem;
`;

const JunePickerCardCTA = styled.span`
  margin-top: auto;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9d6d49;
`;

// ── END JUNE PICKER STYLES ───────────────────────────────────────────────────

const TicketTypesSection = styled.section`
  margin: 0;
  padding: clamp(2.5rem, 6vw, 3.75rem) clamp(1.25rem, 4vw, 2rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  background: linear-gradient(
    180deg,
    ${props => props.theme.colors.offWhite} 0%,
    rgba(241, 239, 234, 0.35) 50%,
    ${props => props.theme.colors.offWhite} 100%
  );
`;

const TicketTypesInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

const TicketTypesHeader = styled.div`
  text-align: center;
  max-width: 36rem;
  margin: 0 auto clamp(1.75rem, 4vw, 2.35rem);
`;

const TicketTypesTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  line-height: 1.15;

  &::after {
    content: '';
    display: block;
    width: 3rem;
    height: 2px;
    margin: 1rem auto 0;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${props => props.theme.colors.accent},
      transparent
    );
  }
`;

const TicketTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TicketTypeCard = styled.article`
  padding: 1.25rem 1.35rem;
  border-radius: 16px;
  border: 1px solid rgba(16, 29, 64, 0.08);
  background: #fff;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 10px 32px rgba(16, 29, 64, 0.06);
`;

const TicketTypeLabel = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.05rem, 2vw, 1.2rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
  line-height: 1.2;
`;

const TicketTypeDetail = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.65vw, 0.95rem);
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.62);
  margin: 0;
`;

const TicketTypesNote = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.62);
  text-align: center;
  max-width: 36rem;
  margin: 1.25rem auto 0;
`;

const DayTickets = () => {
  const { loading: eventsLoading, error: eventsError, data: eventsData } = useQuery(GET_EVENTS_QUERY);
  const { loading: pricingLoading, error: pricingError, data: pricingData } = useQuery(GET_TICKET_PRICING);
  const { loading: settingsLoading, error: settingsError, data: settingsData } = useQuery(GET_SITE_SETTINGS);


  // Default pricing data (fallback if API fails)
  const defaultCastleAndGardensPricing = [
    {
      type: 'Adult',
      onlinePrice: '£23.40',
      officePrice: '£29.99'
    },
    {
      type: 'Child',
      onlinePrice: '£9.00',
      officePrice: '£10.00'
    },
    {
      type: 'Concession',
      onlinePrice: '£21.60',
      officePrice: '£24.00'
    }
  ];

  const defaultGardensAndPlaygroundPricing = [
    {
      type: 'Adult',
      onlinePrice: '£15.00',
      officePrice: '£18.00'
    },
    {
      type: 'Child',
      onlinePrice: '£7.00',
      officePrice: '£8.00'
    },
    {
      type: 'Concession',
      onlinePrice: '£13.00',
      officePrice: '£15.00'
    }
  ];

  const defaultAnimalParkPricing = [
    {
      type: 'Adult',
      onlinePrice: '£4.50',
      officePrice: '£4.50'
    },
    {
      type: 'Child',
      onlinePrice: '£4.50',
      officePrice: '£4.50'
    },
    {
      type: 'Under 3',
      onlinePrice: 'Free',
      officePrice: 'Free'
    }
  ];


  // Extract opening hours with fallback
  const openingHours = settingsData?.siteSettings?.nodes?.[0]?.siteSettingsFields?.openingHours || {
    column1: {
      title: "Castle",
      days: "Mon - Sun",
      times: "10:00 - 17:00",
      note: "Last entry at 16:00"
    },
    column2: {
      title: "Gardens & Playground",
      days: "Mon - Sun",
      times: "09:00 - 17:00",
      note: "Last entry at 16:30"
    },
    column3: {
      title: "Retail Village",
      days: "Mon - Sun",
      times: "9:00 - 17:00",
      note: ""
    }
  };

  const castleOpeningDisplay = getCastleOpeningDisplay(openingHours.castle);

  // Format the data for the OpeningTimes component
  const formattedOpeningHours = {
    column1: {
      title: "Castle",
      days: castleOpeningDisplay.days,
      times: castleOpeningDisplay.times,
      note: castleOpeningDisplay.note
    },
    column2: {
      title: "Gardens & Playground",
      days: openingHours.gardens?.days || "Mon - Sun",
      times: openingHours.gardens?.times || "09:00 - 17:00",
      note: openingHours.gardens?.note || "Last entry at 16:30"
    },
    column3: {
      title: "Retail Village",
      days: openingHours.retail?.days || "Mon - Sun",
      times: openingHours.retail?.times || "9:00 - 17:00",
      note: openingHours.retail?.note || ""
    }
  };

  // Also update the pricing data extraction
  const pricingSettings = pricingData?.siteSettings?.nodes?.[0]?.siteSettingsFields || {};
  const ticketPricing = pricingSettings.ticketPricing || {};

  // Process castle and gardens pricing
  const cgPricing = ticketPricing.castleGardensPricing || {};
  const castleAndGardensPricing = cgPricing ? [
    {
      type: cgPricing.adultType || 'Adult',
      onlinePrice: cgPricing.adultOnlinePrice || '£23.40',
      officePrice: cgPricing.adultOfficePrice || '£26.00'
    },
    {
      type: cgPricing.childType || 'Child',
      onlinePrice: cgPricing.childOnlinePrice || '£9.00',
      officePrice: cgPricing.childOfficePrice || '£10.00'
    },
    {
      type: cgPricing.concessionType || 'Concession',
      onlinePrice: cgPricing.concessionOnlinePrice || '£21.60',
      officePrice: cgPricing.concessionOfficePrice || '£24.00'
    }
  ] : defaultCastleAndGardensPricing;

  // Process gardens pricing
  const gPricing = ticketPricing.gardensPricing || {};
  const gardensAndPlaygroundPricing = gPricing ? [
    {
      type: gPricing.adultType || 'Adult',
      onlinePrice: gPricing.adultOnlinePrice || '£9.00',
      officePrice: gPricing.adultOfficePrice || '£10.00'
    },
    {
      type: gPricing.childType || 'Child',
      onlinePrice: gPricing.childOnlinePrice || '£5.40',
      officePrice: gPricing.childOfficePrice || '£6.00'
    },
    {
      type: gPricing.concessionType || 'Concession',
      onlinePrice: gPricing.concessionOnlinePrice || '£7.20',
      officePrice: gPricing.concessionOfficePrice || '£8.00'
    }
  ] : defaultGardensAndPlaygroundPricing;

  const handleBooking = (ticketType) => {
    if (ticketType === 'gardens') {
      window.location.href = '/booking-journey?locationId=978&pageId=40ca044f-534f-4b7f-83ea-b4156f0633d3';
      return;
    }
    if (ticketType === 'animal-park') {
      window.location.href =
        '/booking-journey?locationId=978&pageId=833fe805-0f3f-4af2-a552-7e86642746ad';
      return;
    }

    // Default: Castle, Gardens & Playground booking journey
    window.location.href = '/booking-journey?locationId=978&pageId=b0f1a62a-a34a-4c4e-a88d-6fda03a19279';
  };

  // Event cards fallback imagery (local assets)
  const customImages = {
    castle: castleImage,
    gardens: gardensImage,
    retail: retailImage,
    playground: playgroundImage
  };

  // Get all events
  const eventsContent = eventsLoading ? [] : 
    eventsData && eventsData.events && eventsData.events.nodes ? 
    processEvents(eventsData.events.nodes, customImages) : [];
  
  // Filter for tours based on the eventType field
  const toursContent = eventsContent.filter(event => 
    event.eventType === 'tour'
  );

  const spotlightEvents =
    toursContent.length > 0
      ? eventsContent.filter(e => e.eventType !== 'tour')
      : eventsContent;
  const mainEventsList =
    spotlightEvents.length > 0 ? spotlightEvents : eventsContent;

  if (pricingLoading) return <p>Loading pricing information...</p>;

  const dayTicketsHero = {
    backgroundImage: DAY_TICKETS_BANNER_CDN,
    compact: true,
    centered: true,
    welcomeText: 'Your visit',
    mainTitle: 'Day Tickets',
    subtitle: 'CASTLE, GARDENS, PLAYGROUND & MORE'
  };

  return (
    <DayTicketsContainer>
      <Hero {...dayTicketsHero} />
      <TicketsIntroSection>
        <TicketsIntroInner>
          <TicketsIntroEyebrow>Choose your ticket</TicketsIntroEyebrow>
          <TicketsIntroTitle>Castle, gardens &amp; add-ons</TicketsIntroTitle>
          <TicketsIntroSubtitle>
            Pick the experience that suits your day. Below are ticket office reference prices; book online for live rates and any Belvoir Group Saver offers at checkout.
          </TicketsIntroSubtitle>
        </TicketsIntroInner>
      </TicketsIntroSection>

      <ImageSlantPricing
        eyebrow="October 2026"
        image="https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp"
        title="Belvoir's Enchanted Halloween Trail"
        description="A wicked curse has fallen on Belvoir. Can your little Curse Breakers solve the mystery, break the spell, and free Stuart's friends before nightfall? A magical Halloween trail through the adventure playground — for the bravest families only."
        buttonText="Book Enchanted Halloween Trail"
        buttonAction={() => { window.location.href = '/halloween-belvoir-castle/tickets'; }}
        imagePosition="right"
      />

      <ImageSlantPricing
        eyebrow="Deck the Halls"
        image={christmasImage}
        title="Christmas at Belvoir"
        description="The magic of Christmas comes to life at Belvoir Castle. From Deck the Halls and Santa's Grotto to festive trails and candlelit evenings — there's something enchanting for the whole family this winter."
        buttonText="Explore Christmas"
        buttonAction={() => { window.location.href = '/christmas'; }}
        imagePosition="left"
      />

      <ImageSlantPricing
        eyebrow="Full experience"
        image={DAY_TICKETS_CASTLE_TICKET_CDN}
        title="Castle, Gardens & Playground"
        description="Experience the majesty of our historic castle and beautiful gardens"
        buttonText="Book now"
        buttonAction={() => window.location.href = '/booking-journey?locationId=978&pageId=b0f1a62a-a34a-4c4e-a88d-6fda03a19279'}
        imagePosition="right"
        pricingData={castleAndGardensPricing}
      />
      <ImageSlantPricing
        eyebrow="Gardens & playground"
        image={DAY_TICKETS_GARDENS_TICKET_CDN}
        title="Gardens & Playground"
        description="Explore our beautiful gardens and let the children enjoy our adventure playground"
        buttonText="Book now"
        buttonAction={() => handleBooking('gardens')}
        imagePosition="left"
        pricingData={gardensAndPlaygroundPricing}
      />
      <ImageSlantPricing
        eyebrow="Retail village"
        image={DAY_TICKETS_ANIMAL_PARK_CDN}
        title="Animal Park"
        description="Meet our friendly animals and enjoy a fun-filled day at the Belvoir Animal Park"
        buttonText="Book now"
        buttonAction={() => handleBooking('animal-park')}
        imagePosition="right"
        pricingData={defaultAnimalParkPricing}
      />
      <TicketTypesSection aria-labelledby="ticket-types-heading">
        <TicketTypesInner>
          <TicketTypesHeader>
            <PackageEyebrowRow>
              <PackageEyebrowLine aria-hidden />
              <PackageEyebrow>Before you book</PackageEyebrow>
            </PackageEyebrowRow>
            <TicketTypesTitle id="ticket-types-heading">Ticket types</TicketTypesTitle>
          </TicketTypesHeader>
          <TicketTypesGrid>
            <TicketTypeCard>
              <TicketTypeLabel>Adult</TicketTypeLabel>
              <TicketTypeDetail>18+</TicketTypeDetail>
            </TicketTypeCard>
            <TicketTypeCard>
              <TicketTypeLabel>Child</TicketTypeLabel>
              <TicketTypeDetail>Over 3–17</TicketTypeDetail>
            </TicketTypeCard>
            <TicketTypeCard>
              <TicketTypeLabel>Under 3</TicketTypeLabel>
              <TicketTypeDetail>Free until 3rd birthday</TicketTypeDetail>
            </TicketTypeCard>
            <TicketTypeCard>
              <TicketTypeLabel>Concession</TicketTypeLabel>
              <TicketTypeDetail>65+ and students (with valid ID)</TicketTypeDetail>
            </TicketTypeCard>
          </TicketTypesGrid>
          <TicketTypesNote>
            From a child&apos;s 3rd birthday they need a Child ticket (over 3–17). Under 3 is free until the 3rd birthday.
          </TicketTypesNote>
        </TicketTypesInner>
      </TicketTypesSection>
      <PackageSection>
        <PackageInner>
          <PackageHeader>
            <PackageEyebrowRow>
              <PackageEyebrowLine aria-hidden />
              <PackageEyebrow>Enhance your visit</PackageEyebrow>
            </PackageEyebrowRow>
            <PackageTitle>Build your perfect day out</PackageTitle>
            <PackageDescription>
              When you book tickets you can add extras and tailor your visit — afternoon tea, tours, the animal park and
              seasonal highlights. Browse below and follow the links for times, prices and availability.
            </PackageDescription>
          </PackageHeader>
          <ExtrasGrid>
            {VISIT_EXTRAS.map(extra => (
              <ExtraCard key={extra.title}>
                <ExtraTitle>{extra.title}</ExtraTitle>
                <ExtraDescription>{extra.description}</ExtraDescription>
                <ExtraLink to={extra.to}>Learn more</ExtraLink>
              </ExtraCard>
            ))}
          </ExtrasGrid>
        </PackageInner>
      </PackageSection>

      <EventsBridgeSection>
        <EventsBridgeInner>
          <EventsBridgeEyebrow>While you&apos;re here</EventsBridgeEyebrow>
          <EventsBridgeTitle>Events &amp; activities</EventsBridgeTitle>
          <EventsBridgeCopy>
            Seasonal workshops, trails, tours and more — see what&apos;s on during your visit or browse the full calendar.
          </EventsBridgeCopy>
          <EventsBridgeButtons>
            <EventsPrimaryBtn href="#event-listings">Explore our events</EventsPrimaryBtn>
            <EventsSecondaryBtn to="/whats-on/events">Events page</EventsSecondaryBtn>
          </EventsBridgeButtons>
        </EventsBridgeInner>
      </EventsBridgeSection>

      {eventsLoading ? (
        <EventsLoadingState id="event-listings">Loading upcoming events…</EventsLoadingState>
      ) : eventsError ? (
        <EventsErrorState id="event-listings">Error loading events: {eventsError.message}</EventsErrorState>
      ) : (
        <EventsListRegion id="event-listings">
          <WhatsOn
            headerEyebrow="Events & activities"
            title="Coming up during your visit"
            events={mainEventsList}
            enableScrolling
            showParklandSection
          />
          {toursContent.length > 0 && (
            <WhatsOn
              title="Daily tours"
              events={toursContent}
              enableScrolling
              showParklandSection={false}
            />
          )}
        </EventsListRegion>
      )}
      <OpeningTimes columns={formattedOpeningHours} />
      <Footer />

    </DayTicketsContainer>
  );
};

export default DayTickets; 